import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval,
  startOfWeek, endOfWeek, isSameMonth, isSameDay, addMonths, subMonths,
} from "date-fns";
import { it as itLocale, enUS, fr as frLocale, es as esLocale, de as deLocale, ja as jaLocale } from "date-fns/locale";
import { ChevronLeft, ChevronRight, X, RotateCcw, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCALES = { it: itLocale, en: enUS, fr: frLocale, es: esLocale, de: deLocale, ja: jaLocale };

const STATUS_COLORS = {
  pending_payment: "bg-[#B89668]/20 text-[#B89668] border-[#B89668]/40",
  pending: "bg-[#C85A3C]/15 text-[#C85A3C] border-[#C85A3C]/30",
  confirmed: "bg-[#6B7A3F]/20 text-[#6B7A3F] border-[#6B7A3F]/40",
  cancelled: "bg-[#5C564F]/15 text-[#5C564F] border-[#5C564F]/30",
  expired: "bg-[#5C564F]/15 text-[#5C564F] border-[#5C564F]/30",
  failed: "bg-[#5C564F]/15 text-[#5C564F] border-[#5C564F]/30",
  abandoned: "bg-[#E8A33D]/20 text-[#E8A33D] border-[#E8A33D]/60 font-semibold",
};

const STATUS_LABELS = {
  pending_payment: "In attesa pagamento",
  pending: "In attesa",
  confirmed: "Confermata",
  cancelled: "Annullata",
  expired: "Scaduta",
  failed: "Fallita",
  abandoned: "Abbandonata",
};

const EMPTY_BOOKING = {
  id: null,
  name: "",
  email: "",
  phone: "",
  class_type: "coppie",
  date: "",
  time_slot: "19:30",
  guests: 2,
  message: "",
  status: "confirmed",
  payment_status: "unpaid",
};

export default function BookingsCalendar({ bookings, token, lang, t, onRefresh }) {
  const locale = LOCALES[lang] || itLocale;
  const [current, setCurrent] = useState(new Date());
  const [modalBooking, setModalBooking] = useState(null); // {booking, mode:'edit'|'create'}
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);

  const start = startOfWeek(startOfMonth(current), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(current), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });

  const bookingsByDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!map[b.date]) map[b.date] = [];
      map[b.date].push(b);
    }
    return map;
  }, [bookings]);

  const activeStatuses = ["pending_payment", "pending", "confirmed"];

  const handleDayClick = (d, hasBookings) => {
    if (hasBookings) return; // click on empty area of a day with pills does nothing to avoid conflicts
    const iso = format(d, "yyyy-MM-dd");
    setModalBooking({ booking: { ...EMPTY_BOOKING, date: iso }, mode: "create" });
  };

  const handleDrop = async (targetDate) => {
    setDropTarget(null);
    if (!dragId) return;
    const src = bookings.find((b) => b.id === dragId);
    if (!src || src.date === targetDate) { setDragId(null); return; }
    setRescheduling(true);
    try {
      await axios.patch(`${API}/admin/bookings/${dragId}`, { date: targetDate }, {
        headers: { "X-Admin-Password": token },
      });
      toast.success("Prenotazione spostata");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore spostamento");
    } finally {
      setDragId(null);
      setRescheduling(false);
    }
  };

  return (
    <div data-testid="bookings-calendar">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button
          data-testid="cal-prev"
          onClick={() => setCurrent(subMonths(current, 1))}
          className="p-2 text-[#FAF9F6]/60 hover:text-[#C85A3C] transition"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h2 className="font-serif-display text-3xl" data-testid="cal-month">
          {format(current, "MMMM yyyy", { locale })}
        </h2>
        <button
          data-testid="cal-next"
          onClick={() => setCurrent(addMonths(current, 1))}
          className="p-2 text-[#FAF9F6]/60 hover:text-[#C85A3C] transition"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <p className="text-[11px] text-[#FAF9F6]/45 mb-3 text-center">
        Clicca su un giorno vuoto per creare una prenotazione · Trascina una prenotazione su un altro giorno per spostarla
      </p>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
          <div key={d} className="text-center text-xs tracking-widest uppercase text-[#FAF9F6]/50 py-2">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
        {days.map((d) => {
          const iso = format(d, "yyyy-MM-dd");
          const dayBookings = bookingsByDate[iso] || [];
          const hasActive = dayBookings.some((b) => activeStatuses.includes(b.status));
          const outOfMonth = !isSameMonth(d, current);
          const today = isSameDay(d, new Date());
          const isDrop = dropTarget === iso;
          return (
            <div
              key={iso}
              data-testid={`cal-day-${iso}`}
              onClick={() => handleDayClick(d, dayBookings.length > 0)}
              onDragOver={(e) => { e.preventDefault(); setDropTarget(iso); }}
              onDragLeave={() => setDropTarget((prev) => (prev === iso ? null : prev))}
              onDrop={(e) => { e.preventDefault(); handleDrop(iso); }}
              className={`relative group min-h-[92px] border p-1.5 rounded-sm transition ${
                outOfMonth ? "border-[#FAF9F6]/5 bg-[#FAF9F6]/[0.02]" : "border-[#FAF9F6]/10 bg-[#FAF9F6]/[0.03]"
              } ${!outOfMonth && dayBookings.length === 0 ? "hover:bg-[#FAF9F6]/[0.08] cursor-pointer" : ""} ${
                isDrop ? "!border-[#C85A3C] !bg-[#C85A3C]/10" : ""
              }`}
            >
              <div className={`text-xs font-medium mb-1 flex items-center justify-between ${
                outOfMonth ? "text-[#FAF9F6]/25" : today ? "text-[#C85A3C]" : "text-[#FAF9F6]/70"
              }`}>
                <span>{format(d, "d")}</span>
                {!outOfMonth && dayBookings.length === 0 && (
                  <Plus className="w-3 h-3 text-[#FAF9F6]/25 opacity-0 group-hover:opacity-100 transition" />
                )}
              </div>
              <div className="space-y-1">
                {dayBookings.map((b) => (
                  <button
                    key={b.id}
                    data-testid={`cal-booking-${b.id}`}
                    draggable
                    onDragStart={(e) => { setDragId(b.id); e.dataTransfer.effectAllowed = "move"; }}
                    onDragEnd={() => setDragId(null)}
                    onClick={(e) => { e.stopPropagation(); setModalBooking({ booking: b, mode: "edit" }); }}
                    className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded-sm border truncate transition cursor-grab active:cursor-grabbing ${STATUS_COLORS[b.status] || STATUS_COLORS.pending} ${
                      dragId === b.id ? "opacity-40" : ""
                    }`}
                    title={`${b.name} · ${b.time_slot} · ${STATUS_LABELS[b.status] || b.status}`}
                  >
                    <span className="font-semibold">{b.time_slot}</span> {b.name.split(" ")[0]}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {rescheduling && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center pointer-events-none">
          <div className="bg-[#2C2520] px-6 py-3 rounded-sm border border-[#C85A3C]/50 text-[#FAF9F6] text-sm">
            Sto spostando la prenotazione…
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-[11px] tracking-widest uppercase">
        {[
          ["pending_payment", "In attesa pagamento"],
          ["pending", "In attesa"],
          ["confirmed", "Confermata"],
          ["cancelled", "Annullata"],
        ].map(([k, l]) => (
          <div key={k} className="flex items-center gap-2">
            <span className={`inline-block w-3 h-3 rounded-sm border ${STATUS_COLORS[k]}`} />
            <span className="text-[#FAF9F6]/55">{l}</span>
          </div>
        ))}
      </div>

      {modalBooking && (
        <BookingEditModal
          booking={modalBooking.booking}
          mode={modalBooking.mode}
          token={token}
          t={t}
          lang={lang}
          onClose={() => setModalBooking(null)}
          onSaved={() => { setModalBooking(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function BookingEditModal({ booking, mode, token, t, lang, onClose, onSaved }) {
  const locale = LOCALES[lang] || itLocale;
  const labels = t.admin;
  const isCreate = mode === "create";
  const [form, setForm] = useState({
    name: booking.name || "",
    email: booking.email || "",
    phone: booking.phone || "",
    class_type: booking.class_type || "coppie",
    date: booking.date || "",
    time_slot: booking.time_slot || "19:30",
    guests: booking.guests || 2,
    message: booking.message || "",
    status: booking.status || "confirmed",
  });
  const [saving, setSaving] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isCreate) {
        if (!form.name || !form.email || !form.phone || !form.date) {
          toast.error("Compila almeno nome, email, telefono e data.");
          setSaving(false);
          return;
        }
        await axios.post(
          `${API}/admin/bookings?status=${encodeURIComponent(form.status)}`,
          { ...form, guests: Number(form.guests), language: lang },
          { headers: { "X-Admin-Password": token } }
        );
        toast.success("Prenotazione creata");
      } else {
        await axios.patch(
          `${API}/admin/bookings/${booking.id}`,
          { ...form, guests: Number(form.guests) },
          { headers: { "X-Admin-Password": token } }
        );
        toast.success(labels.changesSaved || "Modifiche salvate");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const doRefund = async () => {
    if (!window.confirm(labels.refundConfirm || "Vuoi rimborsare €50 e annullare?")) return;
    setRefunding(true);
    try {
      await axios.post(`${API}/admin/bookings/${booking.id}/refund`, {}, {
        headers: { "X-Admin-Password": token },
      });
      toast.success(labels.refundSuccess || "Rimborso effettuato");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore rimborso");
    } finally {
      setRefunding(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-[#FAF9F6]/25 focus:border-[#C85A3C] outline-none px-3 py-2 text-[#FAF9F6] rounded-sm [color-scheme:dark]";
  const labelCls = "block text-xs tracking-widest uppercase text-[#FAF9F6]/55 mb-1";
  const canRefund = !isCreate && booking.payment_status === "paid";

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1F1A14]/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="booking-edit-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#2C2520] border border-[#FAF9F6]/15 rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="overline text-[#C85A3C]">
              {isCreate ? "Nuova prenotazione" : (labels.editBooking || "Modifica prenotazione")}
            </p>
            <h3 className="font-serif-display text-3xl mt-1">
              {form.date ? format(parseISO(form.date), "d MMMM yyyy", { locale }) : "—"}
            </h3>
          </div>
          <button
            data-testid="edit-close"
            onClick={onClose}
            className="text-[#FAF9F6]/50 hover:text-[#C85A3C] transition p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t.booking.name}</label>
            <input data-testid="edit-name" className={inputCls} value={form.name} onChange={(e) => upd("name", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t.booking.email}</label>
            <input data-testid="edit-email" type="email" className={inputCls} value={form.email} onChange={(e) => upd("email", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t.booking.phone}</label>
            <input data-testid="edit-phone" className={inputCls} value={form.phone} onChange={(e) => upd("phone", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t.booking.guests}</label>
            <input data-testid="edit-guests" type="number" min={1} max={12} className={inputCls} value={form.guests} onChange={(e) => upd("guests", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t.booking.date}</label>
            <input data-testid="edit-date" type="date" className={inputCls} value={form.date} onChange={(e) => upd("date", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t.booking.slot}</label>
            <input data-testid="edit-time" type="time" step="900" className={inputCls} value={form.time_slot} onChange={(e) => upd("time_slot", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>{t.booking.classType}</label>
            <select
              data-testid="edit-class"
              value={form.class_type}
              onChange={(e) => upd("class_type", e.target.value)}
              className={inputCls}
            >
              <option value="coppie">{t.booking.coppie}</option>
              <option value="gruppo">{t.booking.gruppo}</option>
              <option value="tartufo">{t.booking.tartufo}</option>
              <option value="degustazione">{t.booking.degustazione}</option>
              <option value="evento">{t.booking.evento}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>{t.booking.message}</label>
            <textarea
              data-testid="edit-message"
              rows={3}
              className={inputCls + " resize-none"}
              value={form.message}
              onChange={(e) => upd("message", e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Stato</label>
            <select
              data-testid="edit-status"
              value={form.status}
              onChange={(e) => upd("status", e.target.value)}
              className={inputCls}
            >
              {["pending_payment", "pending", "confirmed", "cancelled", "expired", "failed", "abandoned"].map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
        </div>

        {!isCreate && booking.payment_status === "paid" && (
          <div className="mt-4 flex items-center gap-2 text-xs text-[#6B7A3F]">
            <span className="inline-block w-2 h-2 rounded-full bg-[#6B7A3F]" />
            Caparra €50 pagata via Stripe
          </div>
        )}
        {!isCreate && booking.payment_status === "refunded" && (
          <div className="mt-4 flex items-center gap-2 text-xs text-[#B89668]">
            <span className="inline-block w-2 h-2 rounded-full bg-[#B89668]" />
            Caparra rimborsata
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          {canRefund ? (
            <button
              data-testid="edit-refund"
              onClick={doRefund}
              disabled={refunding}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-widest uppercase border border-[#C85A3C]/50 text-[#C85A3C] hover:bg-[#C85A3C] hover:text-[#FAF9F6] rounded-sm transition disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              {refunding ? "…" : (labels.refundBooking || "Rimborsa e annulla")}
            </button>
          ) : <span />}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-xs tracking-widest uppercase border border-[#FAF9F6]/25 text-[#FAF9F6]/70 hover:border-[#C85A3C] rounded-sm transition"
            >
              {labels.cancel || "Annulla"}
            </button>
            <button
              data-testid="edit-save"
              onClick={save}
              disabled={saving}
              className="btn-terracotta px-6 py-2.5 rounded-sm tracking-[0.2em] uppercase text-xs disabled:opacity-50"
            >
              {saving ? "…" : (isCreate ? "Crea prenotazione" : (labels.saveChanges || "Salva modifiche"))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
